import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Panel, PanelBody, PanelHeader } from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { EmptyState, ErrorNotice, InfoNotice, LoadingPanel } from '../../components/ui/States';
import ApplicationView from '../../components/review/ApplicationView';
import RubricForm from '../../components/review/RubricForm';
import {
  CalibrationTable,
  DeliberationTable,
  EquitySummary,
  ExportModal,
} from '../../components/review/CommitteeViews';
import { Confidential, StatusPill, inputClass } from '../../components/review/parts';
import logo from '../../assets/divert-logo-dark.svg';
import {
  ApplicationView as View,
  Calibration,
  Conflict,
  Deliberation,
  Queue,
  ReviewerMe,
  SEAT_LABELS,
  Score,
  downloadCsv,
  errorMessage,
  isUnauthorized,
  reviewAuthApi,
  reviewSignInUrl,
  reviewerApi,
  downloadAttachment,
  stateLabel,
} from '../../services/reviewApi';

const CONFLICT_TYPES = [
  { type: 'supervisor', label: "I am the applicant's supervisor", level: 'hard' },
  { type: 'trainee', label: 'The applicant is my current or former trainee', level: 'hard' },
  { type: 'family_personal', label: 'Family or personal relationship', level: 'hard' },
  { type: 'financial', label: 'Financial relationship', level: 'hard' },
  { type: 'coauthor', label: 'We co-authored in the last 3 years', level: 'soft' },
  { type: 'same_institution', label: 'Same institution', level: 'soft' },
];

const SIGN_IN_ERRORS: Record<string, string> = {
  no_access: 'That IBM ID has no seat on an open Fellow selection committee. Contact the Program Manager.',
  signin_failed: 'Sign-in did not complete. Please try again.',
  signin_expired: 'Sign-in took too long. Please try again.',
  signin_unavailable: 'Reviewer sign-in is not configured yet.',
};

function Shell({ me, children }: { me?: ReviewerMe; children: ReactNode }) {
  const queryClient = useQueryClient();
  const signOut = async () => {
    await reviewAuthApi.post('/logout');
    queryClient.removeQueries('reviewer');
    window.location.href = '/review';
  };
  return (
    <div className="min-h-screen">
      <header className="bg-paper text-ink px-4 md:px-8 py-3 flex items-center justify-between gap-4 border-b border-line">
        <Link to="/review" className="flex items-center gap-4 text-ink">
          <img src={logo} alt="DIVERT Mental Health" className="h-[30px] w-auto" />
          <span className="text-[15px] text-ink-2">Fellow review</span>
        </Link>
        {me ? (
          <div className="flex items-center gap-4 text-small">
            <span>{me.name}</span>
            <Button size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        ) : null}
      </header>
      <main className="px-7 pt-7 pb-24 max-w-content">{children}</main>
    </div>
  );
}

function SignIn() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const error = new URLSearchParams(location.search).get('error');
  const [email, setEmail] = useState('');
  const config = useQuery<{ oidc: boolean; devLogin: boolean }>(
    'reviewer-auth-config',
    async () => (await reviewAuthApi.get('/config')).data.data,
  );
  const dev = useMutation(async () => reviewAuthApi.post('/dev-login', { email }), {
    onSuccess: () => queryClient.invalidateQueries('reviewer'),
  });
  return (
    <Shell>
      <div className="max-w-[520px]">
        <h1 className="font-serif font-semibold text-[38px] leading-none">Fellow application review</h1>
        <p className="text-table text-ink-2 mt-3">
          For members of the DIVERT Fellow selection committee. Sign in with your IBM ID; you will see only the
          applications assigned to you.
        </p>
        {error ? (
          <div className="mt-5">
            <ErrorNotice>{SIGN_IN_ERRORS[error] ?? SIGN_IN_ERRORS.signin_failed}</ErrorNotice>
          </div>
        ) : null}
        <div className="mt-6">
          {config.data?.oidc ? (
            <a href={reviewSignInUrl}>
              <Button variant="primary" size="lg">
                Sign in with IBM ID
              </Button>
            </a>
          ) : config.data ? (
            <InfoNotice>Reviewer sign-in is not configured on this server yet.</InfoNotice>
          ) : null}
        </div>
        {config.data?.devLogin ? (
          <div className="mt-8 border-t border-line pt-5">
            <div className="text-small text-ink-2 mb-1">Development sign-in (local only)</div>
            <div className="flex gap-2">
              <input
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="committee member email"
              />
              <Button variant="outline" onClick={() => dev.mutate()}>
                Sign in
              </Button>
            </div>
            {dev.error ? (
              <div className="mt-3">
                <ErrorNotice>{errorMessage(dev.error, 'No seat for that email.')}</ErrorNotice>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

function Home({ me }: { me: ReviewerMe }) {
  if (me.cycles.length === 1) return <Navigate to={`/review/${me.cycles[0].id}`} replace />;
  return (
    <Panel>
      <PanelHeader title="Your selection cycles" />
      {!me.cycles.length ? (
        <EmptyState title="Nothing to review">You have no seat on an open Fellow selection committee.</EmptyState>
      ) : (
        <ul>
          {me.cycles.map((c) => (
            <li key={c.id} className="border-b border-line">
              <Link to={`/review/${c.id}`} className="flex justify-between px-5 py-3 hover:bg-selected">
                <span className="font-semibold">{c.name}</span>
                <span className="text-ink-2 text-small">
                  {stateLabel(c.state)} · {c.seats.map((s) => SEAT_LABELS[s]).join(', ')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function CycleHome() {
  const { cycleId = '' } = useParams();
  const queue = useQuery<Queue>(
    ['reviewer', cycleId, 'queue'],
    async () => (await reviewerApi.get(`/cycles/${cycleId}/queue`)).data.data,
  );
  if (queue.isLoading) return <LoadingPanel label="Loading your applications…" />;
  if (queue.error || !queue.data)
    return <ErrorNotice>{errorMessage(queue.error, 'Could not load this cycle.')}</ErrorNotice>;
  const q = queue.data;
  const committee = q.seats.some((s) => s !== 'reviewer') || ['deliberation', 'decided'].includes(q.cycle.state);
  const items = q.groups.flatMap((g) => g.items);
  const done = items.filter((i) => i.status === 'submitted').length;

  return (
    <div>
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="font-serif font-semibold text-[38px] leading-none">{q.cycle.name}</h1>
          <p className="text-table text-ink-2 mt-2">
            {stateLabel(q.cycle.state)} · {q.seats.map((s) => SEAT_LABELS[s]).join(', ')}
            {items.length ? ` · ${done} of ${items.length} submitted` : ''}
          </p>
        </div>
        {committee ? (
          <Link to={`/review/${cycleId}/committee`}>
            <Button variant="outline">
              {q.seats.includes('chair') || q.seats.includes('co_chair') ? 'Chair & committee' : 'Committee'}
            </Button>
          </Link>
        ) : null}
      </div>
      <div className="mb-5">
        <Confidential />
      </div>
      {!q.seats.includes('reviewer') ? (
        <InfoNotice>You do not review applications in this cycle. The committee view opens at deliberation.</InfoNotice>
      ) : !items.length ? (
        <EmptyState title="No applications yet">
          {q.cycle.state === 'review'
            ? 'Nothing is assigned to you.'
            : 'Applications appear here when the review period opens.'}
        </EmptyState>
      ) : (
        q.groups.map((g) => (
          <Panel key={g.careerStage} className="mb-6">
            <PanelHeader title={g.careerStage} note={`${g.items.length}`} />
            <ul>
              {g.items.map((i) => (
                <li key={i.assignmentId} className="border-b border-line">
                  <Link
                    to={`/review/${cycleId}/applications/${i.applicationId}`}
                    className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-selected"
                  >
                    <span>
                      <span className="font-semibold">{i.applicantName}</span>
                      <span className="block text-small text-ink-2">
                        {[i.institution, i.discipline, i.region].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {i.purpose === 'third' ? <StatusPill status="draft" label="third review" /> : null}
                      <StatusPill
                        status={i.status}
                        label={i.status === 'needs_attestation' ? 'confirm no conflict' : undefined}
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        ))
      )}
      {q.conflictsDeclared ? (
        <p className="text-small text-ink-2">
          {q.conflictsDeclared} application(s) removed after you declared a conflict.
        </p>
      ) : null}
    </div>
  );
}

function ConflictForm({
  onSubmit,
  onCancel,
  submitLabel,
}: {
  onSubmit: (c: { type: string; detail: string }) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}) {
  const [type, setType] = useState('');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const level = CONFLICT_TYPES.find((c) => c.type === type)?.level;
  return (
    <div>
      <label className="block text-small text-ink-2 mb-1">Conflict</label>
      <select className={`${inputClass} mb-3`} value={type} onChange={(e) => setType(e.target.value)}>
        <option value="">Choose…</option>
        {CONFLICT_TYPES.map((c) => (
          <option key={c.type} value={c.type}>
            {c.label}
          </option>
        ))}
      </select>
      {level ? (
        <p className="text-small text-ink-2 mb-3">
          {level === 'hard'
            ? 'A hard conflict: you will not review this applicant, and the application will be reassigned.'
            : 'A soft conflict: you can continue; the chair sees the flag and may ask you to step aside.'}
        </p>
      ) : null}
      <label className="block text-small text-ink-2 mb-1">Anything the chair should know (optional)</label>
      <input className={`${inputClass} mb-4`} value={detail} onChange={(e) => setDetail(e.target.value)} />
      <div className="flex gap-3">
        <Button
          variant="destructive"
          disabled={!type}
          onClick={async () => {
            try {
              await onSubmit({ type, detail });
            } catch (err) {
              setError(errorMessage(err, 'Could not record the conflict.'));
            }
          }}
        >
          {submitLabel}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {error ? (
        <div className="mt-3">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}
    </div>
  );
}

function AttestationGate({
  view,
  cycleId,
  onDone,
}: {
  view: View;
  cycleId: string;
  onDone: (conflicted: boolean) => void;
}) {
  const [declaring, setDeclaring] = useState(false);
  const attest = useMutation(
    async (body: Record<string, unknown>) =>
      (await reviewerApi.post(`/cycles/${cycleId}/assignments/${view.assignmentId}/attest`, body)).data.data,
    { onSuccess: (d: { conflicted: boolean }) => onDone(d.conflicted) },
  );
  const id = view.identity!;
  return (
    <Panel className="max-w-[720px]">
      <PanelHeader title="Before you open this application" />
      <PanelBody>
        <p className="text-table">
          <span className="font-semibold">{id.name}</span> · {id.degreeLevel ?? id.careerStage} · {id.institution}
        </p>
        <p className="text-small text-ink-2 mt-1">Supervisor: {id.supervisorName ?? '—'}</p>
        <p className="text-table mt-4">
          Do you have a conflict of interest with this applicant? Hard conflicts: you supervise them, they are or were
          your trainee, a family or personal relationship, or a financial one. Soft: the same institution, or you
          co-authored in the last 3 years.
        </p>
        {declaring ? (
          <div className="mt-5">
            <ConflictForm
              submitLabel="Declare conflict"
              onCancel={() => setDeclaring(false)}
              onSubmit={async (conflict) => {
                await attest.mutateAsync({ conflict });
              }}
            />
          </div>
        ) : (
          <div className="flex gap-3 mt-5">
            <Button variant="primary" disabled={attest.isLoading} onClick={() => attest.mutate({ noConflict: true })}>
              I have no conflict with this applicant
            </Button>
            <Button variant="outline" onClick={() => setDeclaring(true)}>
              Declare a conflict
            </Button>
          </div>
        )}
        {attest.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(attest.error, 'Could not record your answer.')}</ErrorNotice>
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function CoReviewers({ view }: { view: View }) {
  if (!view.otherReviews) return null;
  return (
    <Panel className="mt-6">
      <PanelHeader title="Other reviewers" />
      <PanelBody>
        {!view.otherReviews.length ? (
          <p className="text-small text-ink-2">No other reviewer on this application.</p>
        ) : null}
        {view.otherReviews.map((r) => (
          <div key={r.id} className="border-b border-line py-2 text-small">
            <span className="font-semibold">{r.reviewer}</span>{' '}
            <span className="text-ink-2">{r.scores ? 'submitted' : 'not submitted yet'}</span>
            {r.scores ? (
              <div className="mt-1">
                {(view.rubric?.criteria ?? []).map((c) => {
                  const s: Score | undefined = r.scores?.[c.key];
                  const v = c.scale === 'score' ? s?.score : c.scale === 'text' ? s?.text : s?.rating;
                  return v === null || v === undefined ? null : (
                    <div key={c.key} className={c.scale === 'text' ? 'whitespace-pre-wrap mt-1' : ''}>
                      <span className="text-ink-2">{c.label}:</span> {String(v)}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ))}
        {view.summary?.flagged ? (
          <p className="text-small text-warning mt-2">
            Priority scores differ by {view.summary.spread}; the chair may follow up.
          </p>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function ReviewApplication() {
  const { cycleId = '', applicationId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [declaring, setDeclaring] = useState(false);
  const key = ['reviewer', cycleId, 'application', applicationId];
  const view = useQuery<View>(
    key,
    async () => (await reviewerApi.get(`/cycles/${cycleId}/applications/${applicationId}`)).data.data,
    { retry: false },
  );
  const reload = () => {
    queryClient.invalidateQueries(key);
    queryClient.invalidateQueries(['reviewer', cycleId, 'queue']);
  };
  const back = (
    <Link to={`/review/${cycleId}`} className="link-underline text-small text-ink-2">
      Your applications
    </Link>
  );
  if (view.isLoading) return <LoadingPanel label="Loading the application…" />;
  if (view.error || !view.data) {
    return (
      <div>
        {back}
        <div className="mt-4">
          <EmptyState title="Not available">
            This application is not assigned to you, or is no longer available to you.
          </EmptyState>
        </div>
      </div>
    );
  }
  const v = view.data;
  if (!v.substance && v.access?.attest) {
    return (
      <div>
        {back}
        <div className="mt-4">
          <AttestationGate
            view={v}
            cycleId={cycleId}
            onDone={(conflicted) => (conflicted ? navigate(`/review/${cycleId}`) : reload())}
          />
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        {back}
        {v.access?.declareConflict && v.review ? (
          <button type="button" className="link-underline text-small" onClick={() => setDeclaring(true)}>
            Declare a conflict
          </button>
        ) : null}
      </div>
      <h1 className="font-serif font-semibold text-[34px] leading-none mb-1">{v.identity?.name}</h1>
      <p className="text-small text-ink-2 mb-5">
        {v.identity?.degreeLevel ?? v.identity?.careerStage} · {v.identity?.institution}
      </p>
      <div className="grid grid-cols-[1fr_480px] gap-6 items-start">
        <Panel>
          <PanelBody>
            <ApplicationView
              view={v}
              onDownload={(fieldId, label) =>
                downloadAttachment(
                  reviewerApi,
                  `/cycles/${cycleId}/applications/${applicationId}/files/${fieldId}`,
                  label,
                )
              }
            />
          </PanelBody>
        </Panel>
        <div className="sticky top-4">
          {v.review && v.rubric ? (
            <Panel>
              <PanelHeader
                title="Your review"
                note={`rubric v${v.review.rubricVersion ?? v.rubric.version} · ${v.review.state}`}
              />
              <PanelBody>
                <RubricForm
                  rubric={v.rubric}
                  review={v.review}
                  extras={v.substance?.extra ?? []}
                  editable={Boolean(v.access?.edit)}
                  onSave={async (scores) => {
                    await reviewerApi.put(`/cycles/${cycleId}/reviews/${v.review!.id}`, { scores });
                  }}
                  onSubmit={async (reason) => {
                    await reviewerApi.post(`/cycles/${cycleId}/reviews/${v.review!.id}/submit`, { reason });
                    reload();
                  }}
                />
              </PanelBody>
            </Panel>
          ) : (
            <InfoNotice>You are not scoring this application.</InfoNotice>
          )}
          <CoReviewers view={v} />
        </div>
      </div>
      {declaring ? (
        <Modal title="Declare a conflict" onClose={() => setDeclaring(false)}>
          <ConflictForm
            submitLabel="Declare conflict"
            onCancel={() => setDeclaring(false)}
            onSubmit={async (c) => {
              const res = (await reviewerApi.post(`/cycles/${cycleId}/applications/${applicationId}/conflict`, c)).data
                .data;
              setDeclaring(false);
              if (res.level === 'hard') navigate(`/review/${cycleId}`);
              else reload();
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function CommitteeRoom({ me }: { me: ReviewerMe }) {
  const { cycleId = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const cycle = me.cycles.find((c) => c.id === cycleId);
  const chair = Boolean(cycle?.seats.some((s) => s === 'chair' || s === 'co_chair'));
  const [tab, setTab] = useState<'deliberation' | 'calibration' | 'conflicts'>(
    chair && cycle?.state !== 'deliberation' && cycle?.state !== 'decided' ? 'calibration' : 'deliberation',
  );
  const [exporting, setExporting] = useState(false);
  const delib = useQuery<Deliberation>(
    ['reviewer', cycleId, 'deliberation'],
    async () => (await reviewerApi.get(`/cycles/${cycleId}/deliberation`)).data.data,
    {
      enabled: tab === 'deliberation',
      retry: false,
    },
  );
  const cal = useQuery<Calibration>(
    ['reviewer', cycleId, 'calibration'],
    async () => (await reviewerApi.get(`/cycles/${cycleId}/chair/calibration`)).data.data,
    {
      enabled: chair && tab === 'calibration',
      retry: false,
    },
  );
  const flags = useQuery<Conflict[]>(
    ['reviewer', cycleId, 'conflicts'],
    async () => (await reviewerApi.get(`/cycles/${cycleId}/chair/conflicts`)).data.data,
    {
      enabled: chair && tab === 'conflicts',
      retry: false,
    },
  );
  const rule = useMutation(
    async ({ id, ruling }: { id: string; ruling: string }) =>
      reviewerApi.post(`/cycles/${cycleId}/chair/conflicts/${id}/rule`, { ruling }),
    { onSuccess: () => queryClient.invalidateQueries(['reviewer', cycleId]) },
  );
  if (!cycle) return <EmptyState title="Not available">You have no seat in this cycle.</EmptyState>;
  const tabs = chair ? (['calibration', 'deliberation', 'conflicts'] as const) : (['deliberation'] as const);
  const open = (id: string) => navigate(`/review/${cycleId}/applications/${id}`);

  return (
    <div>
      <Link to={`/review/${cycleId}`} className="link-underline text-small text-ink-2">
        {cycle.name}
      </Link>
      <div className="flex items-center justify-between mt-2 mb-4">
        <h1 className="font-serif font-semibold text-[34px] leading-none">
          {chair ? 'Chair & committee' : 'Committee'}
        </h1>
        {chair && ['calibration', 'deliberation', 'decided'].includes(cycle.state) ? (
          <Button variant="outline" onClick={() => setExporting(true)}>
            Export
          </Button>
        ) : null}
      </div>
      <div className="mb-4">
        <Confidential />
      </div>
      <div className="flex gap-1 border-b border-line-strong mb-6">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`font-semibold text-small px-4 py-[9px] ${tab === t ? 'bg-ink text-paper' : 'text-ink-2 hover:text-ink'}`}
          >
            {t === 'conflicts' ? 'Soft conflicts' : t}
          </button>
        ))}
      </div>

      {tab === 'calibration' ? (
        cal.isLoading ? (
          <LoadingPanel label="Loading…" />
        ) : cal.data ? (
          <Panel>
            <PanelBody>
              <CalibrationTable
                data={cal.data}
                onOpen={open}
                onReopen={async (reviewId, reason) => {
                  await reviewerApi.post(`/cycles/${cycleId}/chair/reviews/${reviewId}/reopen`, { reason });
                  queryClient.invalidateQueries(['reviewer', cycleId]);
                }}
              />
            </PanelBody>
          </Panel>
        ) : (
          <EmptyState title="Not open yet">Calibration opens when the review period starts.</EmptyState>
        )
      ) : null}

      {tab === 'deliberation' ? (
        delib.isLoading ? (
          <LoadingPanel label="Loading…" />
        ) : delib.data ? (
          <div className="flex flex-col gap-6">
            <Panel>
              <PanelBody>
                <DeliberationTable
                  data={delib.data}
                  onOpen={open}
                  onDecide={
                    chair
                      ? async (row, body) => {
                          await reviewerApi.post(`/cycles/${cycleId}/chair/applications/${row.id}/decision`, body);
                          queryClient.invalidateQueries(['reviewer', cycleId]);
                        }
                      : undefined
                  }
                  onCondition={
                    chair
                      ? async (decisionId, status) => {
                          await reviewerApi.patch(`/cycles/${cycleId}/chair/decisions/${decisionId}/condition`, {
                            status,
                          });
                          queryClient.invalidateQueries(['reviewer', cycleId]);
                        }
                      : undefined
                  }
                />
              </PanelBody>
            </Panel>
            {delib.data.equity ? (
              <Panel>
                <PanelHeader title="Self-identification summary" />
                <PanelBody>
                  <EquitySummary equity={delib.data.equity} />
                </PanelBody>
              </Panel>
            ) : null}
          </div>
        ) : (
          <EmptyState title="Not open yet">The committee view opens at deliberation.</EmptyState>
        )
      ) : null}

      {tab === 'conflicts' ? (
        <Panel>
          <PanelBody>
            {!flags.data?.length ? (
              <p className="text-small text-ink-2">No conflicts to show.</p>
            ) : (
              flags.data.map((f) => (
                <div
                  key={f.id}
                  className="border-b border-line py-2 text-small flex items-center justify-between gap-4"
                >
                  <span>
                    <span className="font-semibold">{f.member}</span> · {f.applicant} · {f.typeLabel} ({f.level}) ·{' '}
                    <span className="text-ink-2">{stateLabel(f.status)}</span>
                    {f.detail ? <span className="block text-ink-2">{f.detail}</span> : null}
                  </span>
                  {f.canRule ? (
                    <span className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => rule.mutate({ id: f.id, ruling: 'allowed' })}>
                        Allow
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => rule.mutate({ id: f.id, ruling: 'treated_as_hard' })}
                      >
                        Treat as hard
                      </Button>
                    </span>
                  ) : null}
                </div>
              ))
            )}
            {rule.error ? <ErrorNotice>{errorMessage(rule.error, 'Could not record the ruling.')}</ErrorNotice> : null}
          </PanelBody>
        </Panel>
      ) : null}

      {exporting ? (
        <ExportModal
          allowSelfId={cycle.state === 'deliberation'}
          onClose={() => setExporting(false)}
          onExport={async (body) => {
            const res = await reviewerApi.post(`/cycles/${cycleId}/chair/export`, body, { responseType: 'blob' });
            downloadCsv(res.data, 'fellow-selection.csv', res.headers['content-disposition']);
          }}
        />
      ) : null}
    </div>
  );
}

export default function ReviewerPortal() {
  const me = useQuery<ReviewerMe>(['reviewer', 'me'], async () => (await reviewerApi.get('/me')).data.data, {
    retry: false,
  });
  if (me.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingPanel label="Loading…" height={200} />
      </div>
    );
  }
  if (me.error && isUnauthorized(me.error)) return <SignIn />;
  if (me.error || !me.data) {
    return (
      <Shell>
        <ErrorNotice>{errorMessage(me.error, 'The review portal is unavailable.')}</ErrorNotice>
      </Shell>
    );
  }
  return (
    <Shell me={me.data}>
      <Routes>
        <Route path="/review" element={<Home me={me.data} />} />
        <Route path="/review/:cycleId" element={<CycleHome />} />
        <Route path="/review/:cycleId/applications/:applicationId" element={<ReviewApplication />} />
        <Route path="/review/:cycleId/committee" element={<CommitteeRoom me={me.data} />} />
        <Route path="*" element={<Navigate to="/review" replace />} />
      </Routes>
    </Shell>
  );
}
